import { createRef } from "react";

import { DEFAULT_NOTE_COLOR } from "~/constants";
import { fromPx, toPx } from "~/utils/components";
import { logger } from "~/utils/logging";

import { MediaPlayerFullscreenWindowPosition } from "../media/types";

import type { CSSProperties } from "react";
import type { NoteEditorData } from "../noteEditorModal/NoteEditorModal";
import type { MediaPlayerContextValue } from "../media/context";
import type { WatchViewLayoutEvent } from "./WatchView";

export enum WatchViewSubview {
  Notebook = "notebook",
  Transcript = "transcript",
  Completed = "completed",
  TeacherNotes = "teacherNotes",
  Reflection = "reflection",
  Downloads = "downloads",
  Slides = "slides",
}

export enum WatchViewViewMode {
  Freestyle = "freestyle",
  Fullscreen = "fullscreen",
  Splitscreen = "splitscreen",
}

export enum WatchViewMediaMode {
  Video = "video",
  Audio = "audio",
}

export enum WatchViewComputedCustomProperties {
  AudioPlayerHeight = "--watchview-audio-player-height",
  DrawerWidth = "--watchview-drawer-width",
  SlideLinksInsetLeft = "--watchview-slide-links-inset-left",
  SlideLinksInsetTop = "--watchview-slide-links-inset-top",
  SlidesMinHeight = "--watchview-slides-min-height",
  StageHeight = "--watchview-stage-height",
}

export interface WatchViewManagerInit {
  defaultState?: Partial<WatchViewManagerState>;
  onMediaModeChange?: (event: { value: WatchViewMediaMode }) => void;
  onSubviewChange?: (event: { value: WatchViewSubview | null }) => void;
  onUpdate?: () => void;
  onViewModeChange?: (event: { value: WatchViewViewMode }) => void;
}

export interface WatchViewManagerState {
  /**
   * The current playback time (HTMLMediaElement#currentTime).
   */
  currentTime: number;
  /**
   * Color of the note currently being drafted.
   */
  draftNoteColor: NoteEditorData["color"];
  /**
   * Timestamp of the note currently being drafted.
   */
  draftNoteTimestamp: number | null;
  /**
   * Text value of the note currently being drafted.
   */
  draftNoteValue: NoteEditorData["value"] | null;
  /**
   * Whether or not the main session video/audio has ended.
   */
  ended: boolean;
  /**
   * The position of the secondary window when in fullscreen mode.
   */
  fullscreenWindowPosition: MediaPlayerFullscreenWindowPosition;
  /**
   * Whether or not the slides are the main window in fullscreen mode.
   */
  fullscreenSlides: boolean;
  /**
   * Whether or not the note editor is open.
   */
  isNoteEditorOpen: boolean;
  /**
   * Media playback mode - video or audio.
   */
  mediaMode: WatchViewMediaMode;
  /**
   * Ratio representing the splitscreen video:slides split.
   */
  splitscreenVideoSize: number;
  /**
   * The initial starting time for the media player.
   */
  startTime: number;
  /**
   * Which, if any, subview is currently displayed. These
   * are rendered in the "drawer".
   */
  subview: WatchViewSubview | null;
  /**
   * Ratio representing the size of the subview drawer.
   */
  subviewSize: number;
  /**
   * The "viewing mode" being used. Determines how the UI is
   * laid out.
   */
  viewMode: WatchViewViewMode;
}

export const DEFAULT_STATE: WatchViewManagerState = {
  currentTime: 0,
  draftNoteColor: DEFAULT_NOTE_COLOR,
  draftNoteTimestamp: null,
  draftNoteValue: null,
  ended: false,
  fullscreenWindowPosition: MediaPlayerFullscreenWindowPosition.bottomLeft,
  fullscreenSlides: false,
  isNoteEditorOpen: false,
  mediaMode: WatchViewMediaMode.Video,
  splitscreenVideoSize: 0.5,
  subview: null,
  subviewSize: 0.2,
  startTime: 0,
  viewMode: WatchViewViewMode.Splitscreen,
};

const SLIDE_ASPECT_RATIO = 16 / 9;

export class WatchViewManager {
  noteSaveRequestQueue = new Map();

  playerContextRef = createRef<MediaPlayerContextValue>();

  // Computed CSS properties
  customProperties = new Map<
    WatchViewComputedCustomProperties,
    string | undefined
  >([
    [WatchViewComputedCustomProperties.AudioPlayerHeight, "0px"],
    [WatchViewComputedCustomProperties.StageHeight, "0px"],
    [WatchViewComputedCustomProperties.SlidesMinHeight, "0px"],
    [WatchViewComputedCustomProperties.DrawerWidth, "0px"],
  ]);

  state: WatchViewManagerState = DEFAULT_STATE;

  updateCount = 0;

  onMediaModeChange: WatchViewManagerInit["onMediaModeChange"];
  onSubviewChange: WatchViewManagerInit["onSubviewChange"];
  onUpdate: WatchViewManagerInit["onUpdate"];
  onViewModeChange: WatchViewManagerInit["onViewModeChange"];

  constructor(init?: WatchViewManagerInit) {
    // Set the initial state
    if (init?.defaultState) {
      this.state = {
        ...this.state,
        ...init.defaultState,
        startTime: init.defaultState.currentTime ?? this.state.startTime,
      };
    }

    this.onMediaModeChange = init?.onMediaModeChange;
    this.onSubviewChange = init?.onSubviewChange;
    this.onUpdate = init?.onUpdate;
    this.onViewModeChange = init?.onViewModeChange;
  }

  get styles() {
    return Object.fromEntries(this.customProperties.entries()) as CSSProperties;
  }

  update() {
    this.onUpdate?.();
  }

  /**
   * Updates the manager's state. Functions exactly like
   * Component#setState.
   */
  setState(
    nextState:
      | Partial<WatchViewManagerState>
      | ((prevState: WatchViewManagerState) => Partial<WatchViewManagerState>),
  ) {
    const prevState = this.state;

    this.state = {
      ...prevState,
      ...(typeof nextState === "function" ? nextState(this.state) : nextState),
    };

    this.update();

    return this.state;
  }

  /**
   * Sets the current currentTime.
   */
  setCurrentTime(time: number) {
    this.setState({ currentTime: Math.floor(time) });
  }

  /**
   * Sets the current startTime.
   */
  setStartTime(time: number) {
    this.setState({ startTime: Math.floor(time) });
  }

  /**
   * Sets the current subview.
   */
  setSubview(subview: WatchViewSubview | null) {
    this.setState({ subview });
    this.onSubviewChange?.({ value: subview });
  }

  /**
   * Sets the view mode.
   */
  setViewMode(viewMode: WatchViewViewMode) {
    this.setState({ viewMode });
    this.onViewModeChange?.({ value: viewMode });
  }

  /**
   * Sets the view mode
   */
  setMediaMode(mediaMode: WatchViewMediaMode) {
    this.setState({ mediaMode });
    this.onMediaModeChange?.({ value: mediaMode });
  }

  /**
   * Sets the note editor open/closed.
   */
  setNoteEditorOpen(isOpen: boolean) {
    this.setState({ isNoteEditorOpen: isOpen });
  }

  /**
   * Sets the timestamp for a the new note.
   */
  setDraftNoteTimestamp(timestamp: number) {
    this.setState({ draftNoteTimestamp: timestamp });
  }

  /**
   *  Sets draft note data.
   */
  setDraftNoteData(noteData: NoteEditorData) {
    this.setState({
      draftNoteColor: noteData.color,
      draftNoteValue: noteData.value,
    });
  }

  /**
   * Clears draft note data.
   */
  clearDraftNoteData() {
    this.setState({
      draftNoteValue: null,
    });
  }

  /**
   * Sets the position of the secondary window when in fullscreen mode.
   */
  setFullscreenWindowPosition(position: MediaPlayerFullscreenWindowPosition) {
    this.setState({
      fullscreenWindowPosition: position,
    });
  }

  /**
   * Sets (or unsets) the slides as the primary window in
   * fullscreen mode.
   */
  setFullscreenSlides(fullscreenSlides: boolean) {
    this.setState({
      fullscreenSlides,
    });
  }

  /**
   * Sets whether or not the main session video/audio has
   * ended.
   */
  setEnded(ended: boolean) {
    this.setState({ ended });
  }

  /**
   * Exits Picture-in-picture video mode.
   */
  async exitPictureInPicture() {
    if (document.pictureInPictureElement) {
      try {
        await globalThis.document.exitPictureInPicture();
      } catch (error) {
        logger.error("Failed to exit PiP", error);
      }
    }
  }

  handleStageLayout(event: WatchViewLayoutEvent) {
    this.customProperties.set(
      WatchViewComputedCustomProperties.StageHeight,
      toPx(event.DOMRect.height),
    );

    this.update();
  }

  handleSlidesLayout(event: WatchViewLayoutEvent) {
    const { paddingInline = "0px" } = getComputedStyle(event.element);

    this.customProperties.set(
      WatchViewComputedCustomProperties.SlidesMinHeight,
      toPx(
        Math.max(
          (event.DOMRect.width - fromPx(paddingInline) * 2) /
            SLIDE_ASPECT_RATIO,
          200,
        ),
      ),
    );

    this.customProperties.set(
      WatchViewComputedCustomProperties.SlideLinksInsetTop,
      toPx(event.DOMRect.height + event.element.offsetTop + 16),
    );

    this.customProperties.set(
      WatchViewComputedCustomProperties.SlideLinksInsetLeft,
      toPx(event.DOMRect.left),
    );

    this.update();
  }

  handleDrawerLayout(event: WatchViewLayoutEvent) {
    this.customProperties.set(
      WatchViewComputedCustomProperties.DrawerWidth,
      toPx(event.DOMRect.width),
    );

    this.update();
  }

  handleAudioLayout(event: WatchViewLayoutEvent) {
    this.customProperties.set(
      WatchViewComputedCustomProperties.AudioPlayerHeight,
      toPx(event.DOMRect.height),
    );

    this.update();
  }
}
